import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Copy, Download } from "lucide-react";

import { Button } from "@components/ui/Button";
import { Input } from "@components/ui/Input";

import { adminPost } from "@lib/adminApi";

import type { CreateGiftBatchResponse } from "./types";

// The two products an admin can issue. Defaults to the full bundle; "series"
// (Essentials) is the single-season option. These match the backend's
// REGULAR_SKUS allowlist.
const PRODUCT_OPTIONS = [
  {
    value: "series,series2,series3",
    label: "Essentials + Explore More (all seasons)",
  },
  { value: "series", label: "Essentials (Season 1 only)" },
];

const MAX_BATCH_SIZE = 1000;

// Where a redeemer goes. Override per-environment via VITE_REDEEM_BASE_URL;
// defaults to production.
const REDEEM_BASE_URL =
  import.meta.env.VITE_REDEEM_BASE_URL ?? "https://www.omgyes.com/redeem";

// Quote a CSV cell when it contains a comma, quote, or newline. Product values
// like "series,series2,series3" contain commas, so this is required.
//
// Also guard against CSV formula injection: these files go to clinics and get
// opened in Excel/Sheets, where a cell starting with = + - @ (or tab/CR) is
// evaluated as a formula. The free-text batchId is the main vector. Prefix any
// such cell with a single quote so it's treated as literal text.
function csvCell(value: string): string {
  const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

function downloadCsv(result: CreateGiftBatchResponse): void {
  const header = ["code", "product", "content", "batch_id", "redeem_url"];
  const rows = result.codes.map((c) => [
    c.code,
    result.product,
    result.explicit ? "explicit" : "non-explicit",
    result.batchId,
    `${REDEEM_BASE_URL}?code=${encodeURIComponent(c.code)}`,
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `gift-batch-${result.batchId}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const CreateGiftBatch = () => {
  const [count, setCount] = useState(1);
  const [product, setProduct] = useState(PRODUCT_OPTIONS[0].value);
  // true = explicit, false = non-explicit (the default, since non-explicit is
  // now the standard for all users). Drives the redeemer's explicit content
  // eligibility/visibility on the backend.
  const [explicit, setExplicit] = useState(false);
  const [batchId, setBatchId] = useState("admin");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateGiftBatchResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedBatchId = batchId.trim();
    if (!trimmedBatchId) {
      setError("Batch id is required.");
      return;
    }
    if (!Number.isInteger(count) || count < 1 || count > MAX_BATCH_SIZE) {
      setError(`Count must be a whole number between 1 and ${MAX_BATCH_SIZE}.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await adminPost<CreateGiftBatchResponse>(
        "/admin/gifts/batch",
        { count, purchaseOption: product, batchId: trimmedBatchId, explicit },
      );
      setResult(res);
    } catch (err) {
      console.error("Failed to create gift batch:", err);
      setError("Failed to create the batch. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyAllCodes = () => {
    if (!result) return;
    navigator.clipboard
      .writeText(result.codes.map((c) => c.code).join("\n"))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setError("Couldn't copy to clipboard — try again."));
  };

  const startAnother = () => {
    setResult(null);
    setBatchId("admin");
    setError(null);
  };

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div className="flex items-center gap-2">
        <Link to="/gifts" className="p-1 hover:bg-gray-100 rounded">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <h2 className="text-2xl font-bold">Generate gift codes</h2>
      </div>
      <p className="text-sm text-gray-500">
        Generate one or more redeemable gift codes — a single code, or a batch
        to hand to a clinic on a spreadsheet. Codes are tagged with the batch id
        so they can be told apart from gifts bought through checkout.
      </p>

      {!result && (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 border rounded-lg bg-white p-6"
        >
          <div className="flex flex-col gap-1">
            <label
              htmlFor="batchId"
              className="text-sm font-medium text-gray-700"
            >
              Batch id
            </label>
            <Input
              id="batchId"
              placeholder="e.g. dr-smith-conf-2026"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              maxLength={100}
            />
            <span className="text-xs text-gray-400">
              A label that groups this batch and marks it as admin-created.
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="product"
              className="text-sm font-medium text-gray-700"
            >
              Product
            </label>
            <select
              id="product"
              className="border rounded-md px-3 py-2 text-sm"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            >
              {PRODUCT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="explicit"
              className="text-sm font-medium text-gray-700"
            >
              Content
            </label>
            <select
              id="explicit"
              className="border rounded-md px-3 py-2 text-sm"
              value={explicit ? "explicit" : "non-explicit"}
              onChange={(e) => setExplicit(e.target.value === "explicit")}
            >
              <option value="non-explicit">Non-explicit (default)</option>
              <option value="explicit">Explicit (opt-in)</option>
            </select>
            <span className="text-xs text-gray-400">
              Non-explicit: recipient can't see or turn on explicit content
              (like a standard new account). Explicit: adds the explicit toggle,
              but content still starts off — the recipient opts in.
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="count"
              className="text-sm font-medium text-gray-700"
            >
              Number of codes
            </label>
            <Input
              id="count"
              type="number"
              min={1}
              max={MAX_BATCH_SIZE}
              value={count}
              // Clearing the field yields NaN; coerce to 0 so the controlled
              // input never gets value={NaN} (React warns). Submit validation
              // then surfaces the 1–1000 message.
              onChange={(e) =>
                setCount(
                  Number.isNaN(e.target.valueAsNumber)
                    ? 0
                    : e.target.valueAsNumber,
                )
              }
              className="max-w-[160px]"
            />
            <span className="text-xs text-gray-400">
              Up to {MAX_BATCH_SIZE} per batch.
            </span>
          </div>

          {error && (
            <div className="px-4 py-2 rounded text-sm bg-red-50 text-red-700 border border-red-200">
              {error}
            </div>
          )}

          <div>
            <Button type="submit" isLoading={submitting} disabled={submitting}>
              Generate codes
            </Button>
          </div>
        </form>
      )}

      {result && (
        <div className="flex flex-col gap-4 border rounded-lg bg-white p-6">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="h-5 w-5" />
            <span className="font-medium">
              Created {result.count} code{result.count === 1 ? "" : "s"}
            </span>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-gray-500">Batch id</dt>
            <dd className="font-medium">{result.batchId}</dd>
            <dt className="text-gray-500">Product</dt>
            <dd className="font-medium">{result.productDisplayName}</dd>
            <dt className="text-gray-500">Content</dt>
            <dd className="font-medium">
              {result.explicit ? "Explicit" : "Non-explicit"}
            </dd>
          </dl>

          <div className="flex gap-2">
            <Button onClick={() => downloadCsv(result)}>
              <Download className="h-4 w-4" /> Download CSV
            </Button>
            <Button variant="secondary" onClick={copyAllCodes}>
              {copied ? (
                <>
                  <Check className="h-4 w-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" /> Copy all codes
                </>
              )}
            </Button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">
                      #
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">
                      Gift Code
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.codes.map((c, i) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2">
                        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                          {c.code}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="secondary" onClick={startAnother}>
              Generate more codes
            </Button>
            <Link
              to="/gifts"
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Back to gifts
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
